import Analytics from '@monolith/legacy/services/analytics';
import { SignUpEventNames } from '@monolith/legacy/services/analytics/events/sign-up/common/event-names';
import SignUpEvent from '@monolith/legacy/services/analytics/events/sign-up/sign-up-event';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';

/**
 * Brand-registration funnel tracking (SEL-3). Every funnel click shares the same
 * `component` and `page_name`; only the action (and optional validity payload)
 * varies. Used by the header CTAs, the sign-up popin and the registration form.
 */
export function trackBrandRegistrationClick(action: string, extra: Record<string, unknown> = {}): Promise<void> {
  return Analytics.track(
    new UserClick({
      component: 'brand_registration',
      action,
      page_name: window.location.pathname,
      ...extra,
    })
  );
}

/**
 * The `is_valid`/`reason` payload for form-submit clicks: track every click
 * (valid or not) so the funnel captures drop-off; on failure, report which
 * fields are invalid.
 */
export function validityTrackingPayload(isValid: boolean, invalidFields: string[]): Record<string, unknown> {
  return {
    is_valid: isValid,
    reason: isValid ? null : invalidFields,
  };
}

/**
 * The brand registration form's funnel steps: reaching a step of the form (i.e.
 * clearing the email-verification gate) and having a lead accepted.
 *
 * Named SignUpEvents rather than UserClicks so they join the shared email and
 * verification-code steps in one event family; `audience: 'brand'` is stated
 * here because this form exists only in the brand funnel, which makes the whole
 * funnel a single Amplitude chart filtered on `audience = brand`.
 *
 * The `type` values match the rest of the family: every other `…Submitted`
 * event is 'Click or Form Submit' (bare 'Form Submit' is reserved for the
 * terminal Sign Up Completed), so a segment cutting the funnel by type keeps
 * the brand steps in.
 *
 * Both of the form's steps report as Viewed under the one event name, told apart
 * by `step`, so a step-2 funnel row is the same event filtered on `step = 2`.
 *
 * These are impressions, not unique views: the emitting watcher fires on every
 * step transition, so going Back or being bounced to step 1 by a server error
 * reports step 1 again. Read the funnel on unique users, as the retailer and
 * brand-onboarding funnels already are — their steps are separate components and
 * remount (and so re-report) on the same navigation.
 */
export function trackBrandFormViewed(step: 1 | 2): void {
  Analytics.track(
    new SignUpEvent(SignUpEventNames.BrandFormViewedEvent, { type: 'Element Viewed', audience: 'brand', step })
  );
}

export function trackBrandFormSubmitted(): Promise<void> {
  return Analytics.track(
    new SignUpEvent(SignUpEventNames.BrandFormSubmittedEvent, { type: 'Click or Form Submit', audience: 'brand' })
  );
}
